import { useMemo, useState } from "react"
import { BookOpenIcon, CalculatorIcon, FileTextIcon,LogOutIcon, NotebookPenIcon, SaveIcon, SearchIcon, SettingsIcon, SparklesIcon } from "lucide-react"
import { estimateTokenCount } from "tokenx"
import { EditableLabel } from "@/components/editable-text/editable-label"
import { EditableText } from "@/components/editable-text/editable-text"
import { handlebarsAutocomplete } from "@/components/editable-text/handlebars"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { CardProps, FlowGroups } from "./items"
import { ItemShell } from "./itemshell"
import { ModelSelect } from "./modelselect"
import { OutcomeMenu } from "./outcomemenu"
import { RemoveButton } from "./removebutton"
import { SearchDialog } from "./searchdialog"
import { SetVarsSection } from "./setvarssection"

type SearchConfig = Extract<PlaybookToolConfig, { type: "search" }>
type GenerateConfig = Extract<PlaybookToolConfig, { type: "generate" }>

const ROW = "rounded-md border border-foreground/25 bg-muted/40 px-2 py-1 flex items-center gap-2"

export function AiItemCard({ item, vars, groups, index, issues, onUpdate, onRemove }: CardProps) {
  const templateExtensions = useMemo(() => [handlebarsAutocomplete(vars)], [vars])
  const transitions = item.transitions ?? []
  const references = item.references ?? []
  const captures = item.captures ?? []
  const tools = item.tools ?? []

  function updateTool(i: number, next: PlaybookToolConfig) {
    onUpdate({ tools: tools.map((t, j) => j === i ? next : t) })
  }

  function removeTool(i: number) {
    onUpdate({ tools: tools.filter((_, j) => j !== i) })
  }

  return <ItemShell item={item} vars={vars} index={index} issues={issues} onUpdate={onUpdate} onRemove={onRemove} icon="🧠">
    <div className="text-xs font-medium text-muted-foreground">Say</div>
    <EditableText value={item.say ?? ""} onChange={say => onUpdate({ say: say || undefined })} extensions={templateExtensions} placeholder="No message" />

    <div className="text-xs font-medium text-muted-foreground">Prompt</div>
    <EditableText value={item.prompt ?? ""} onChange={prompt => onUpdate({ prompt })} placeholder="No prompt" />
    <div className="text-xs text-muted-foreground text-right">{estimateTokenCount(item.prompt ?? "")} tokens</div>

    <div className="text-xs font-medium text-muted-foreground">Context</div>
    <EditableText value={item.context ?? ""} onChange={context => onUpdate({ context: context || undefined })} extensions={templateExtensions} placeholder="No context" className="text-xs" plain />

    {transitions.map((transition, i) => <TransitionRow
      key={i}
      transition={transition}
      groups={groups}
      onChange={next => onUpdate({ transitions: transitions.map((t, j) => j === i ? next : t) })}
      onRemove={() => onUpdate({ transitions: transitions.filter((_, j) => j !== i) })}
    />)}

    {references.map((reference, i) => <ReferenceRow
      key={i}
      reference={reference}
      onChange={next => onUpdate({ references: references.map((r, j) => j === i ? next : r) })}
      onRemove={() => onUpdate({ references: references.filter((_, j) => j !== i) })}
    />)}

    {captures.map((capture, i) => <CaptureRow
      key={i}
      capture={capture}
      onChange={next => onUpdate({ captures: captures.map((c, j) => j === i ? next : c) })}
      onRemove={() => onUpdate({ captures: captures.filter((_, j) => j !== i) })}
    />)}

    {tools.map((tool, i) => {
      if (tool.type === "search") return <SearchRow key={i} tool={tool} onChange={next => updateTool(i, next)} onRemove={() => removeTool(i)} />
      if (tool.type === "generate") return <GenerateRow key={i} tool={tool} onChange={next => updateTool(i, next)} onRemove={() => removeTool(i)} />
      if (tool.type === "pdf") return <div key={i} className={ROW}>
        <FileTextIcon className="size-4 text-muted-foreground" />
        <EditableLabel value={tool.description} onChange={description => updateTool(i, { ...tool, description })} placeholder="What document the model can send" className="flex-1 text-sm" />
        <RemoveButton what="pdf" name={tool.description} empty={!tool.description} onRemove={() => removeTool(i)} />
      </div>
      return <div key={i} className={ROW}>
        {tool.type === "notes" ? <NotebookPenIcon className="size-4 text-muted-foreground" /> : <CalculatorIcon className="size-4 text-muted-foreground" />}
        <span className="flex-1 text-sm">{tool.type === "notes" ? "Notepad: the model keeps notes for this item and for the playbook" : "Calculator: exact arithmetic instead of a guess"}</span>
        <RemoveButton what="tool" name={tool.type} empty onRemove={() => removeTool(i)} />
      </div>
    })}
  </ItemShell>
}

function TransitionRow({ transition, groups, onChange, onRemove }: { transition: PlaybookTransition, groups: FlowGroups, onChange: (transition: PlaybookTransition) => void, onRemove: () => void }) {
  const [open, setOpen] = useState(false)
  const active = Object.keys(transition.set ?? {}).length > 0 || Object.keys(transition.inc ?? {}).length > 0

  return <div className={ROW}>
    <LogOutIcon className="size-4 text-muted-foreground" />
    <EditableLabel value={transition.description} onChange={description => onChange({ ...transition, description })} placeholder="When the model should leave" className="flex-1 text-sm" />
    <OutcomeMenu outcome={transition.action} groups={groups} onChange={action => onChange({ ...transition, action })} />
    <Button variant="ghost" size="icon-sm" aria-label="Transition settings" className={cn(active && "text-primary font-bold")} onClick={() => setOpen(true)}><SettingsIcon /></Button>
    <RemoveButton what="transition" name={transition.description} empty={!transition.description && !active} onRemove={onRemove} />
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Transition settings</DialogTitle>
          <DialogDescription>Written when the model takes this way out, before it moves</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <SetVarsSection set={transition.set ?? {}} onChange={set => onChange({ ...transition, set: Object.keys(set).length ? set : undefined })} />
          <SetVarsSection set={transition.inc ?? {}} onChange={inc => onChange({ ...transition, inc: Object.keys(inc).length ? inc : undefined })} counter />
        </div>
      </DialogContent>
    </Dialog>
  </div>
}

function ReferenceRow({ reference, onChange, onRemove }: { reference: PlaybookReference, onChange: (reference: PlaybookReference) => void, onRemove: () => void }) {
  const [open, setOpen] = useState(false)

  return <div className={ROW}>
    <BookOpenIcon className="size-4 text-muted-foreground" />
    <EditableLabel value={reference.description} onChange={description => onChange({ ...reference, description })} placeholder="When the model should read it" className="flex-1 text-sm" />
    <Button variant="outline" size="sm" onClick={() => setOpen(true)}>{estimateTokenCount(reference.text)} tokens</Button>
    <RemoveButton what="reference" name={reference.description} empty={!reference.description && !reference.text} onRemove={onRemove} />
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{reference.description || "Untitled reference"}</DialogTitle>
          <DialogDescription>Handed to the model only when it asks for it.</DialogDescription>
        </DialogHeader>
        <EditableText value={reference.text} onChange={text => onChange({ ...reference, text })} placeholder="No text" />
      </DialogContent>
    </Dialog>
  </div>
}

function CaptureRow({ capture, onChange, onRemove }: { capture: PlaybookCapture, onChange: (capture: PlaybookCapture) => void, onRemove: () => void }) {
  return <div className="rounded-md border border-foreground/25 bg-muted/40 px-2 py-1 flex flex-col gap-1">
    <div className="flex items-center gap-2">
      <SaveIcon className="size-4 text-muted-foreground" />
      <EditableLabel value={capture.var} onChange={name => onChange({ ...capture, var: name.trim() })} placeholder="var name" className="text-sm font-medium" />
      <Select value={capture.type} onValueChange={type => onChange({ ...capture, type: type as PlaybookCapture["type"], values: type === "enum" ? capture.values ?? [] : undefined })}>
        <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{["text", "number", "boolean", "enum"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      <div className="flex-1" />
      <RemoveButton what="capture" name={capture.var} empty={!capture.var && !capture.description} onRemove={onRemove} />
    </div>
    {capture.type === "enum" && <Input
      value={(capture.values ?? []).join(", ")}
      onChange={e => onChange({ ...capture, values: e.target.value.split(",").map(v => v.trim()) })}
      onBlur={() => onChange({ ...capture, values: (capture.values ?? []).filter(Boolean) })}
      placeholder="Allowed values, comma separated"
      className="h-7 text-xs"
    />}
    <EditableLabel value={capture.description} onChange={description => onChange({ ...capture, description })} placeholder="When and what the model should save" className="text-xs text-muted-foreground" />
  </div>
}

function SearchRow({ tool, onChange, onRemove }: { tool: SearchConfig, onChange: (tool: SearchConfig) => void, onRemove: () => void }) {
  const [open, setOpen] = useState(false)
  const count = tool.sources?.length ?? 0

  return <div className={ROW}>
    <SearchIcon className="size-4 text-muted-foreground" />
    <EditableLabel value={tool.description} onChange={description => onChange({ ...tool, description })} placeholder="What the model can find here" className="flex-1 text-sm" />
    <Button variant="outline" size="sm" onClick={() => setOpen(true)}>{count === 1 ? "1 source" : `${count} sources`}</Button>
    <RemoveButton what="search" name={tool.description} empty={!tool.description && !count} onRemove={onRemove} />
    <SearchDialog open={open} onOpenChange={setOpen} title={tool.description} sources={tool.sources ?? []} onChange={sources => onChange({ ...tool, sources })} />
  </div>
}

function GenerateRow({ tool, onChange, onRemove }: { tool: GenerateConfig, onChange: (tool: GenerateConfig) => void, onRemove: () => void }) {
  const [open, setOpen] = useState(false)

  return <div className={ROW}>
    <SparklesIcon className="size-4 text-muted-foreground" />
    <EditableLabel value={tool.description} onChange={description => onChange({ ...tool, description })} placeholder="What the model can have written" className="flex-1 text-sm" />
    <Button variant="outline" size="sm" onClick={() => setOpen(true)}>{estimateTokenCount(tool.prompt)} tokens</Button>
    <RemoveButton what="generator" name={tool.description} empty={!tool.description && !tool.prompt} onRemove={onRemove} />
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tool.description || "Untitled generator"}</DialogTitle>
          <DialogDescription>Its text goes straight to the reply, after the model's own. Several run at once</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">Model</h3>
          <ModelSelect value={tool.model} onChange={model => onChange({ ...tool, model })} placeholder="The playbook's model" />
          <h3 className="text-sm font-medium">Prompt</h3>
          <EditableText value={tool.prompt} onChange={prompt => onChange({ ...tool, prompt })} placeholder="No prompt" />
        </div>
      </DialogContent>
    </Dialog>
  </div>
}
